import React, { useEffect, useState } from "react";
import api from "../api/api";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Grid,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import Header from "../components/Header";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Button from "@mui/material/Button";

dayjs.extend(duration);

const calculateTimeLeft = (dueDate) => {
  const now = dayjs();
  const expirationDate = dayjs(dueDate);
  const diff = expirationDate.diff(now);

  return dayjs.duration(diff);
};

function Promotions() {
  const [produtos, setProdutos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPromos = async () => {
      setIsLoading(true);
      try {
        const res = await api.get(
          "products?customerPriceNewMin=1&customerPriceNewMax=9999999999"
        );
        if (res.status === 200) {
          setProdutos(res.data.products);
          console.log(res.data.products);
        } else {
          setProdutos([]);
        }
      } catch (error) {
        setProdutos([]);
        console.error("Erro ao carregar as promoções:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPromos();
  }, []);

  // Função para verificar se uma promoção está ativa
  const isPromotionActive = (dueDate) => {
    return dueDate === null || dayjs(dueDate).isAfter(dayjs());
  };

  // Divida as promoções em ativas e encerradas
  const activePromotions = produtos.filter(
    (product) =>
      isPromotionActive(product.discountCustomerDueDate) ||
      isPromotionActive(product.discountVendorDueDate)
  );

  const expiredPromotions = produtos.filter(
    (product) =>
      !isPromotionActive(product.discountCustomerDueDate) &&
      !isPromotionActive(product.discountVendorDueDate)
  );

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl shadow-lg">
      <Header category="Promoções" title="Produtos em Promoção" />

      {isLoading ? (
        <div className="flex flex-col items-center justify-center h-64">
          <CircularProgress size={60} />
          <p className="mt-4 text-gray-600">Carregando promoções...</p>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <p className="text-blue-800 font-medium">
                Total de promoções encontradas:{" "}
                <span className="font-bold">{produtos.length}</span>
              </p>
            </div>
            <div className="flex items-center gap-6 mt-2 text-sm text-blue-700">
              <span>
                Ativas:{" "}
                <span className="font-semibold">{activePromotions.length}</span>
              </span>
              <span>
                Encerradas:{" "}
                <span className="font-semibold">
                  {expiredPromotions.length}
                </span>
              </span>
            </div>
          </div>

          <Accordion
            defaultExpanded
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
              sx={{
                backgroundColor: "#f0fdf4",
                borderBottom: "1px solid #e5e7eb",
                "&:hover": { backgroundColor: "#ecfdf5" },
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <Typography
                  variant="h6"
                  className="text-green-800 font-semibold"
                >
                  Promoções Ativas ({activePromotions.length})
                </Typography>
              </div>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 3 }}>
              {activePromotions.length > 0 ? (
                <Grid container spacing={3}>
                  {activePromotions.map((product) => (
                    <Grid item key={product.id} xs={12} sm={6} md={4} lg={3}>
                      <PromotionProductCard product={product} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-4">
                    <svg
                      className="w-16 h-16 mx-auto"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-lg">
                    Nenhuma promoção ativa encontrada
                  </p>
                </div>
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2-content"
              id="panel2-header"
              sx={{
                backgroundColor: "#fef3f2",
                borderBottom: "1px solid #e5e7eb",
                "&:hover": { backgroundColor: "#fef2f2" },
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <Typography variant="h6" className="text-red-800 font-semibold">
                  Promoções Encerradas ({expiredPromotions.length})
                </Typography>
              </div>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 3 }}>
              {expiredPromotions.length > 0 ? (
                <Grid container spacing={3}>
                  {expiredPromotions.map((product) => (
                    <Grid item key={product.id} xs={12} sm={6} md={4} lg={3}>
                      <PromotionProductCard product={product} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-4">
                    <svg
                      className="w-16 h-16 mx-auto"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-lg">
                    Nenhuma promoção encerrada encontrada
                  </p>
                </div>
              )}
            </AccordionDetails>
          </Accordion>
        </div>
      )}
    </div>
  );
}

export default Promotions;

const PromotionProductCard = ({ product }) => {
  const [customerTimeLeft, setCustomerTimeLeft] = useState(
    product.discountCustomerDueDate
      ? calculateTimeLeft(product.discountCustomerDueDate)
      : null
  );
  const [vendorTimeLeft, setVendorTimeLeft] = useState(
    product.discountVendorDueDate
      ? calculateTimeLeft(product.discountVendorDueDate)
      : null
  );

  useEffect(() => {
    if (product.discountCustomerDueDate || product.discountVendorDueDate) {
      const timer = setInterval(() => {
        setCustomerTimeLeft(
          product.discountCustomerDueDate
            ? calculateTimeLeft(product.discountCustomerDueDate)
            : null
        );
        setVendorTimeLeft(
          product.discountVendorDueDate
            ? calculateTimeLeft(product.discountVendorDueDate)
            : null
        );
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [product.discountCustomerDueDate, product.discountVendorDueDate]);

  const formatTimeLeft = (duration) => {
    if (!duration || duration.asSeconds() <= 0) return null;

    const parts = [];
    if (duration.months()) parts.push(`${duration.months()}M`);
    if (duration.weeks()) parts.push(`${duration.weeks()}S`);
    if (duration.days()) parts.push(`${duration.days()}D`);
    if (duration.hours()) parts.push(`${duration.hours()}H`);
    if (duration.minutes()) parts.push(`${duration.minutes()}m`);
    if (duration.seconds()) parts.push(`${duration.seconds()}s`);
    return parts.join(" ");
  };

  const isExpired = (duration) => duration && duration.asSeconds() <= 0;

  const {
    name,
    imgUrl,
    customerPrice,
    customerPriceNew,
    vendorPrice,
    vendorPriceNew,
  } = product;

  const formatCurrency = (value) => {
    const numberValue = Number(value);
    if (isNaN(numberValue)) {
      return "R$ 0,00";
    }
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numberValue);
  };

  const hasCustomerDiscount =
    customerPriceNew > 0 && customerPriceNew < customerPrice;
  const hasVendorDiscount = vendorPriceNew > 0 && vendorPriceNew < vendorPrice;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 hover:border-blue-300">
      <div className="aspect-w-1 aspect-h-1 overflow-hidden bg-gray-50">
        <img
          className="w-full h-48 object-cover object-center"
          src={
            imgUrl?.[0] ||
            "https://via.placeholder.com/300x300?text=Imagem+não+disponível"
          }
          alt={name}
          onError={(e) => {
            e.target.src =
              "https://via.placeholder.com/300x300?text=Imagem+não+disponível";
          }}
        />
      </div>

      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 line-clamp-2">
          {name}
        </h3>

        <div className="space-y-3">
          {/* Preços do Cliente */}
          {customerPrice > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">
                Preços para Cliente
              </h4>
              <div className="space-y-1">
                <p className="text-sm text-gray-700">
                  Original:{" "}
                  <span className="font-medium">
                    {formatCurrency(customerPrice)}
                  </span>
                </p>
                {hasCustomerDiscount && (
                  <p className="text-sm text-green-700 font-semibold">
                    Promocional:{" "}
                    <span className="font-bold">
                      {formatCurrency(customerPriceNew)}
                    </span>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Preços do Salão */}
          {vendorPrice > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <h4 className="text-sm font-semibold text-purple-800 mb-2">
                Preços para Salão
              </h4>
              <div className="space-y-1">
                <p className="text-sm text-gray-700">
                  Original:{" "}
                  <span className="font-medium">
                    {formatCurrency(vendorPrice)}
                  </span>
                </p>
                {hasVendorDiscount && (
                  <p className="text-sm text-green-700 font-semibold">
                    Promocional:{" "}
                    <span className="font-bold">
                      {formatCurrency(vendorPriceNew)}
                    </span>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Datas de Expiração */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">
              Datas de Expiração
            </h4>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-600">Cliente: </span>
                <span
                  className={
                    product.discountCustomerDueDate
                      ? "text-red-600 font-medium"
                      : "text-gray-500"
                  }
                >
                  {product.discountCustomerDueDate
                    ? dayjs(product.discountCustomerDueDate).format(
                        "DD/MM/YYYY"
                      )
                    : "Sem data de expiração"}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Para Salão: </span>
                <span
                  className={
                    product.discountVendorDueDate
                      ? "text-red-600 font-medium"
                      : "text-gray-500"
                  }
                >
                  {product.discountVendorDueDate
                    ? dayjs(product.discountVendorDueDate).format("DD/MM/YYYY")
                    : "Sem data de expiração"}
                </span>
              </div>
            </div>
          </div>

          {/* Contadores de Tempo */}
          {(customerTimeLeft || vendorTimeLeft) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <h4 className="text-sm font-semibold text-red-800 mb-2">
                Tempo Restante
              </h4>
              <div className="space-y-1 text-sm">
                {customerTimeLeft && !isExpired(customerTimeLeft) && (
                  <p className="text-red-700">
                    Cliente:{" "}
                    <span className="font-bold">
                      {formatTimeLeft(customerTimeLeft)}
                    </span>
                  </p>
                )}
                {vendorTimeLeft && !isExpired(vendorTimeLeft) && (
                  <p className="text-red-700">
                    Para Salão:{" "}
                    <span className="font-bold">
                      {formatTimeLeft(vendorTimeLeft)}
                    </span>
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
